"use client";

import { useId, useMemo } from "react";

import { buildProjectCover } from "@/app/(application)/(ttv)/ttv/components/ttvApplication/controls/modals/projectCover/buildProjectCover";
import {
  COVER_BLUR_STANDARD_DEVIATION,
  COVER_CENTER,
  COVER_CORNER_RADIUS,
  COVER_VIEW_BOX_SIZE,
} from "@/app/(application)/(ttv)/ttv/components/ttvApplication/controls/modals/projectCover/constants";
import { cn } from "@/utils/cn";

// Maps the noise to mid grey with its strength in the alpha channel, so soft-light blending adds texture without shifting the colour of the orb.
const GRAIN_COLOR_MATRIX =
  "0 0 0 0 0.5  0 0 0 0 0.5  0 0 0 0 0.5  0.7 0.7 0.7 0 -0.75";

// A generated cover: soft colour fields and fine grain, with no darkened rim, because on a square cover a rim reads as a drop shadow around every thumbnail. Decorative, so it is hidden from screen readers; the control it sits under names itself. Size comes from className at the call site.
export function ProjectCoverOrb({
  seedText,
  className,
}: {
  seedText: string;
  className: string;
}) {
  // Several orbs share one page and SVG ids are document-global: without a per-instance id every orb would clip and blur through the definitions of the first one.
  const instanceId = useId();
  const clipId = `${instanceId}-clip`;
  const blurId = `${instanceId}-blur`;
  const grainId = `${instanceId}-grain`;
  const cover = useMemo(() => buildProjectCover(seedText), [seedText]);

  return (
    <svg
      aria-hidden
      viewBox={`0 0 ${COVER_VIEW_BOX_SIZE} ${COVER_VIEW_BOX_SIZE}`}
      className={cn("block shrink-0 text-black", className)}
    >
      <defs>
        <clipPath id={clipId}>
          <rect
            x="0"
            y="0"
            rx={COVER_CORNER_RADIUS}
            ry={COVER_CORNER_RADIUS}
            width={COVER_VIEW_BOX_SIZE}
            height={COVER_VIEW_BOX_SIZE}
          />
        </clipPath>
        <filter
          id={blurId}
          x="-60%"
          y="-60%"
          width="220%"
          height="220%"
          colorInterpolationFilters="sRGB"
        >
          <feGaussianBlur stdDeviation={COVER_BLUR_STANDARD_DEVIATION} />
        </filter>
        <filter id={grainId} x="0" y="0" width="100%" height="100%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="1.6"
            numOctaves="3"
            seed={cover.grainSeed}
          />
          <feColorMatrix type="matrix" values={GRAIN_COLOR_MATRIX} />
        </filter>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect
          width={COVER_VIEW_BOX_SIZE}
          height={COVER_VIEW_BOX_SIZE}
          fill={cover.bodyColor}
        />
        <g
          filter={`url(#${blurId})`}
          transform={`rotate(${cover.rotationDegrees} ${COVER_CENTER} ${COVER_CENTER})`}
        >
          {cover.blobs.map((blob, blobIndex) => (
            <ellipse
              // The blobs are a fixed-length list derived from the seed and never reorder, so the index is a stable identity here.
              key={blobIndex}
              cx={blob.centerX}
              cy={blob.centerY}
              rx={blob.radiusX}
              ry={blob.radiusY}
              fill={blob.color}
              opacity={blob.opacity}
            />
          ))}
        </g>
        <rect
          width={COVER_VIEW_BOX_SIZE}
          height={COVER_VIEW_BOX_SIZE}
          filter={`url(#${grainId})`}
          opacity="0.9"
          className="mix-blend-soft-light"
        />
      </g>
    </svg>
  );
}
